import { Button, Col, Form, FormInstance, Row, Select, Space, Switch } from "antd";
import { StoreValue } from "antd/lib/form/interface";
import { useEffect, useMemo, useRef, useState } from "react";
import { AdvertRegulation, AdvertRegulationInput, AdvertRegulationUserOverrides, TaxAdvert } from "../types";
import { renderErrorMessage } from "../../../../../components/messages/errorMessage";
import _t from "../../../../../lang/translate";
import format from "../../../../../utilities/formatNumbers";
import InputNum from "../../../../../components/form/inputNum";
import InputPrice from "../../../../../components/form/inputPrice";
import Icon from "../../../../../assets/icons/icon";
import { SelectProps } from "antd/es";
import calculateAdvertRegulation from "../calculateAdvertRegulation";
import { isValidNum } from "../../../../../utilities/typeGuard";

type AdvertRegulationsProps = {
  advert: TaxAdvert;
  regulations: AdvertRegulation | null | undefined
  setRegulations: React.Dispatch<React.SetStateAction<AdvertRegulation | null | undefined>>
}

type RegulationFormValues = Omit<AdvertRegulationUserOverrides, 'ageRegulation' | 'additionalRegulations'> & {
  ageRegulation: AdvertRegulationUserOverrides['ageRegulation'] & {
    pricePercent?: number | null;
  };
  additionalRegulations: (AdvertRegulationUserOverrides['additionalRegulations'][number] & {
    pricePercent?: number | null;
  })[];
}

type AdditionalRegulationFormValue = RegulationFormValues['additionalRegulations'][number]

export default function AdvertRegulations({ advert, regulations, setRegulations }: AdvertRegulationsProps) {
  const result = regulations?.output;
  const [additionalRegulation, setAdditionalRegulation] = useState<number | undefined>(undefined)
  const [regulationForm] = Form.useForm<RegulationFormValues>()
  const addRef = useRef<(defaultValue?: StoreValue, insertIndex?: number) => void>()

  function getPercentFromPrice(priceRegulation: number | null | undefined) {
    const salesPrice = advert.price?.amount ?? 1;
    let percent: number | null = (priceRegulation ?? 0) / salesPrice
    percent = percent * 100
    percent = Math.round(percent * 100) / 100
    return isValidNum(priceRegulation) ? percent : null
  }

  function getPriceFromPercent(pricePercent: number | null) {
    const salesPrice = advert.price?.amount ?? 1;
    const percent = (pricePercent ?? 0) / 100;
    return salesPrice * percent
  }

  function getInitialValues(overrides: AdvertRegulationUserOverrides | null | undefined): RegulationFormValues | undefined {
    if (!overrides) return undefined

    const initialValues: RegulationFormValues = {
      ...overrides,
      ageRegulation: { ...overrides.ageRegulation },
      additionalRegulations: overrides.additionalRegulations.map<AdditionalRegulationFormValue>(regulation => ({ ...regulation }))
    }

    if (isValidNum(initialValues.ageRegulation.price) && !isValidNum(initialValues.ageRegulation.pricePercent)) {
      initialValues.ageRegulation.pricePercent = getPercentFromPrice(initialValues.ageRegulation.price)
    }

    initialValues?.additionalRegulations?.forEach(regulation => {
      if (isValidNum(regulation.price) && !isValidNum(regulation.pricePercent)) {
        regulation.pricePercent = getPercentFromPrice(regulation.price)
      }
    })

    return initialValues
  }

  function getOverrides(values: RegulationFormValues): AdvertRegulationUserOverrides {
    return {
      plates: values.plates,
      ageRegulation: {
        active: values.ageRegulation.active,
        price: values.ageRegulation.price,
        mileage: values.ageRegulation.mileage
      },
      additionalRegulations: values.additionalRegulations.map(regulation => ({
        active: regulation.active,
        price: regulation.price,
        regulationReason: regulation.regulationReason,
        regulationReasonId: regulation.regulationReasonId
      }))
    }
  }

  const initialValues = useMemo(
    () => getInitialValues(regulations?.input?.overrides),
    [advert.price?.amount, regulations?.input?.overrides]
  );

  useEffect(() => {
    if (!initialValues) return
    regulationForm.setFieldsValue(initialValues)
  }, [initialValues, regulationForm])

  function handleValuesChange(changed: Partial<RegulationFormValues>, values: RegulationFormValues) {
    try {
      if (changed?.ageRegulation) {
        if ('pricePercent' in changed.ageRegulation) {
          const priceRegulation = getPriceFromPercent(changed.ageRegulation.pricePercent as number | null)
          values.ageRegulation.price = priceRegulation
          regulationForm.setFieldsValue({
            ageRegulation: {
              price: priceRegulation
            }
          })
        } else if ('price' in changed.ageRegulation) {
          const priceRegulation = changed.ageRegulation.price as number | null
          const percent = getPercentFromPrice(priceRegulation)
          values.ageRegulation.pricePercent = percent
          regulationForm.setFieldsValue({
            ageRegulation: {
              pricePercent: percent
            }
          })
        }
      }
      if (changed?.additionalRegulations) {
        changed.additionalRegulations.forEach((regulation, index) => {
          if (!regulation) return
          if ('pricePercent' in regulation) {
            const priceRegulation = getPriceFromPercent(regulation.pricePercent as number | null)
            values.additionalRegulations[index].price = priceRegulation
            regulationForm.setFields([{
              name: ['additionalRegulations', index, 'price'],
              value: priceRegulation
            }])
          } else if ('price' in regulation) {
            const percent = getPercentFromPrice(regulation.price as number | null)
            values.additionalRegulations[index].pricePercent = percent
            regulationForm.setFields([{
              name: ['additionalRegulations', index, 'pricePercent'],
              value: percent
            }])
          }
        })
      }
      const overrides = getOverrides(values)
      const input: AdvertRegulationInput = {
        ...regulations?.input,
        overrides
      }
      const result = calculateAdvertRegulation(input)
      setRegulations((prev) => ({
        ...prev,
        input: {
          ...prev?.input,
          overrides: {
            plates: {
              active: overrides.plates.active,
              price: overrides.plates.price ?? result.plates
            },
            additionalRegulations: overrides.additionalRegulations,
            ageRegulation: {
              active: overrides.ageRegulation.active,
              price: overrides.ageRegulation.price ?? result.ageRegulatedPriceDifference,
              mileage: overrides.ageRegulation.mileage ?? result.ageRegulatedMileageDifference
            }
          }
        },
        output: result
      }))
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  function handleAdditionRegulation() {
    try {
      if (!additionalRegulation) throw new Error("Der skal være valgt en reguleringsårsag")
      const additionalRegulations = regulationForm.getFieldValue(['additionalRegulations']) as AdvertRegulationUserOverrides['additionalRegulations']
      const alreadyExists = additionalRegulations.some(reg => reg.regulationReasonId === additionalRegulation)
      if (alreadyExists) throw new Error("Reguleringsårsagen er allerede valgt")
      addRef.current && addRef.current({
        active: true,
        regulationReason: regulationReasons?.find(item => item.value === additionalRegulation)?.label,
        regulationReasonId: additionalRegulation,
        price: undefined,
      })
      setAdditionalRegulation(undefined)
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  return (
    <div>
      <Form form={regulationForm} onValuesChange={handleValuesChange} initialValues={initialValues ?? {}}>
        <Row wrap={false} gutter={[8, 8]} className="bg-light pt-05 pb-05 pl-2 pr-2">
          <Col flex="80px" className="text-bold text-smallest">
            {_t("active")}
          </Col>
          <Col flex={"1 1 40%"} className="text-bold text-smallest" title={_t("regulations")}>
            {_t("regulations")}
          </Col>
          <Col flex={"1 1 23%"} className="text-bold text-smallest">
            {_t("mileage")}
          </Col>
          <Col flex={"1 1 37%"} className="text-bold text-smallest">
            {_t("price")}
          </Col>
        </Row>
        <Row wrap={false} gutter={[8, 8]} className="pt-05 pb-05 pl-2 pr-2 border-bottom">
          <Col flex="80px">
          </Col>
          <Col flex={"1 1 40%"} className="text-ellipsis" title={_t("advert_values")}>
            {_t("advert_values")}
          </Col>
          <Col flex={"1 1 23%"}>
            {format.milage(advert.mileage)}
          </Col>
          <Col flex={"1 1 37%"}>
            {format.price(advert.price?.amount)}
          </Col>
        </Row>
        <Row wrap={false} gutter={[8, 8]} className="pt-05 pb-05 pl-2 pr-2 border-bottom">
          <Col flex="80px">
            <Form.Item valuePropName="checked" name={["ageRegulation", "active"]} noStyle>
              <Switch />
            </Form.Item>
          </Col>
          <Col flex={"1 1 40%"} className="text-ellipsis" title={_t("model_year")}>
            {_t("model_year")}
          </Col>
          <Col flex={"1 1 23%"}>
            <Form.Item name={["ageRegulation", "mileage"]} noStyle>
              <InputNum suffix="km." />
            </Form.Item>
          </Col>
          <Col flex={"1 1 37%"}>
            <Space size={"small"}>
              <Form.Item name={["ageRegulation", "price"]} noStyle>
                <InputNum suffix="kr." />
              </Form.Item>
              <div style={{ width: 76 }}>
                <Form.Item name={["ageRegulation", "pricePercent"]} noStyle>
                  <InputPrice suffix="%" />
                </Form.Item>
              </div>
            </Space>
          </Col>
        </Row>
        <Row wrap={false} gutter={[8, 8]} className="pt-05 pb-05 pl-2 pr-2 border-bottom">
          <Col flex="80px">
            <Form.Item valuePropName="checked" name={["plates", "active"]} noStyle>
              <Switch />
            </Form.Item>
          </Col>
          <Col flex={"1 1 40%"} className="text-ellipsis" title={_t("number_plate_regulation")}>
            {_t("number_plate_regulation")}
          </Col>
          <Col flex={"1 1 23%"}>
          </Col>
          <Col flex={"1 1 37%"}>
            <Form.Item name={["plates", "price"]} noStyle>
              <InputPrice suffix="kr." />
            </Form.Item>
          </Col>
        </Row>
        <Form.List name="additionalRegulations">
          {(fields, { add, remove }) => {
            addRef.current = add
            return (
              <div>
                {fields.map(field => (
                  <Row key={field.key} wrap={false} gutter={[8, 8]} className="pt-05 pb-05 pl-2 pr-2 border-bottom">
                    <Col flex="80px">
                      <Form.Item valuePropName="checked" name={[field.name, "active"]} noStyle>
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col flex={"1 1 40%"}>
                      <Row className="w-100" gutter={[8, 8]} align="middle" wrap={false}>
                        <Col flex="0 0 auto" style={{ maxWidth: "calc(100% - 29px)" }} className="text-ellipsis">
                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const reason = getFieldValue(['additionalRegulations', field.name, 'regulationReason'])
                              return (
                                <span title={reason} style={{ maxWidth: 140 }}>{reason}</span>
                              )
                            }}
                          </Form.Item>
                        </Col>
                        <Col flex="21px">
                          <Icon name="trash-bin-outline" onClick={() => remove(field.name)} />
                        </Col>
                      </Row>
                    </Col>
                    <Col flex={"1 1 23%"}>
                    </Col>
                    <Col flex={"1 1 37%"}>
                      <Space size={"small"}>
                        <Form.Item name={[field.name, "price"]} noStyle>
                          <InputPrice suffix="kr." />
                        </Form.Item>
                        <div style={{ width: 76 }}>
                          <Form.Item name={[field.name, "pricePercent"]} noStyle>
                            <InputPrice suffix="%" />
                          </Form.Item>
                        </div>
                      </Space>
                    </Col>
                  </Row>

                ))}
              </div>
            )
          }}
        </Form.List>

        <Row wrap={false} gutter={[8, 8]} className="pt-05 pb-05 pl-2 pr-2 border-bottom">
          <Col flex="80px">
          </Col>
          <Col flex={"1 1 40%"} className="text-bold text-ellipsis" title={_t("regulated_advert_values")}>
            {_t("regulated_advert_values")}
          </Col>
          <Col flex={"1 1 23%"} className="text-bold">
            {format.milage(Math.round(result?.ageRegulatedMileage ?? 0))}
          </Col>
          <Col flex={"1 1 37%"} className="text-bold">
            {format.price(result?.tradePriceLevel)}
          </Col>
        </Row>
      </Form>
      <Row className="mt-05" gutter={[8, 8]} align="middle">
        <Col flex="0 0 auto">Angiv reguleringstype: </Col>
        <Col flex="1 1 0%">
          <Select showSearch optionFilterProp="label" allowClear style={{ width: "100%" }}
            value={additionalRegulation}
            onChange={(value) => setAdditionalRegulation(value)}
            onClear={() => setAdditionalRegulation(undefined)}
            placeholder={_t("choose_reason")}
            options={regulationReasons?.toSorted(
              (a, b) => a?.label?.toString()?.localeCompare(b?.label?.toString() ?? "") ?? 0
            )}
          />
        </Col>
        <Col flex="0 0 auto">
          <Button disabled={!additionalRegulation} onClick={handleAdditionRegulation}>{_t("add", "regulation")}</Button>
        </Col>
      </Row>
    </div>
  )
}

export const regulationReasons: SelectProps<number>['options'] = [
  { value: 5, label: "Afvigende motorydelse" },
  { value: 6, label: "Afvigende variant" },
  { value: 7, label: "Afvigende karrosseri" },
  { value: 8, label: "Afvigende udstyrsvariant" },
  { value: 9, label: "Afvigelser" },
  { value: 10, label: "For mange liggedage" },
  { value: 11, label: "Købers salær" },
  { value: 12, label: "Facelift" },
  { value: 13, label: "Afvigende gearkasse" },
  { value: 14, label: "To-/firehjulstræk" },
  { value: 15, label: "Lev. omk./Reg. omk." },
  { value: 16, label: "Regulering for prisudvikling" },
]
